/**
 * data/cours/permisAmThemes/index.js — Livret de révision Permis AM
 *
 * 12 infographies pédagogiques couvrant tout le programme du permis AM.
 * Utilisé par la page /permis-am/themes (route publique élève + enseignant).
 *
 * Cette 2e série (par rapport aux 12 visuels de la formation voiturette)
 * fonctionne comme un MÉMO transversal : chaque fiche est autoportante et
 * synthétise un thème du programme.
 */

const IMG_BASE = '/images/permis-am-themes';

export const AM_THEMES = [
  {
    id: 1,
    slug: 'signalisation',
    src: `${IMG_BASE}/01-signalisation.png`,
    title: 'La signalisation',
    subtitle: 'Comprendre et respecter les règles',
    category: 'code',
    keywords: ['panneaux', 'interdiction', 'obligation', 'indication'],
    caption: 'Panneaux d\'interdiction · obligation · indication — la signalisation donne des règles à respecter pour rouler en sécurité.',
    retenir: 'La signalisation donne des règles à respecter pour rouler en sécurité.',
    color: '#a855f7',
  },
  {
    id: 2,
    slug: 'regles-circulation',
    src: null, // pas d'image PNG — rendu HTML/CSS via composant custom
    customRender: 'regles-circulation-am',
    title: 'Les règles de circulation',
    subtitle: 'En France, je roule à droite',
    category: 'code',
    keywords: ['voies', 'positionnement', 'droite', 'dépassement'],
    caption: 'En France, la circulation se fait sur le côté droit de la chaussée — je me positionne pour être bien vu.',
    retenir: 'Je roule à droite. Je me positionne pour être bien vu.',
    color: '#0ea5e9',
  },
  {
    id: 3,
    slug: 'priorites',
    src: `${IMG_BASE}/03-priorites.png`,
    title: 'Les priorités',
    subtitle: 'Identifier les règles aux intersections',
    category: 'code',
    keywords: ['STOP', 'céder', 'feux', 'carrefour'],
    caption: 'Carrefour à droite · STOP · Cédez le passage · Feux tricolores — je repère le type d\'intersection et j\'applique la règle.',
    retenir: 'Je repère le type d\'intersection et j\'applique la règle de priorité.',
    color: '#f97316',
  },
  {
    id: 4,
    slug: 'distances-securite',
    src: `${IMG_BASE}/04-distances-securite.png`,
    title: 'Les distances de sécurité',
    subtitle: 'Pour avoir le temps de réagir',
    category: 'circulation',
    keywords: ['distances', '2 secondes', 'temps de réaction'],
    caption: 'Règle des 2 secondes : point fixe, laisser passer le véhicule, compter « un-mille, deux-mille ».',
    retenir: 'Plus ma vitesse est élevée, plus la distance doit être grande.',
    color: '#22c55e',
  },
  {
    id: 5,
    slug: 'adapter-allure',
    src: null, // pas d'image PNG — rendu HTML/CSS via composant custom
    customRender: 'allure-am',
    title: 'Adapter son allure',
    subtitle: 'Rouler à 45 km/h max — spécial Permis AM',
    category: 'circulation',
    keywords: ['vitesse', '45 km/h', 'AM', 'voiturette', 'cyclomoteur'],
    caption: '45 km/h max · limite par construction · adapte-toi à la météo et aux virages.',
    retenir: '45 km/h, c\'est TA limite — observe, anticipe, laisse-toi doubler.',
    color: '#f43f5e',
  },
  {
    id: 6,
    slug: 'virages',
    src: `${IMG_BASE}/06-virages.png`,
    title: 'Les virages',
    subtitle: 'Regarder, anticiper, bien se positionner',
    category: 'circulation',
    keywords: ['virage', 'trajectoire', 'positionnement'],
    caption: 'Je regarde loin · je me positionne · j\'adapte ma vitesse · je reprends ma trajectoire.',
    retenir: 'Je regarde loin, je ralentis avant le virage et je garde une trajectoire sûre.',
    color: '#a855f7',
  },
  {
    id: 7,
    slug: 'changements-direction',
    src: `${IMG_BASE}/07-changements-direction.png`,
    title: 'Les changements de direction',
    subtitle: 'Tourner en sécurité',
    category: 'circulation',
    keywords: ['clignotant', 'rétros', 'trajectoire'],
    caption: '1. Vérifie rétros · 2. Mets clignotant · 3. Positionne · 4. Tourne en sécurité.',
    retenir: 'Je préviens, je me positionne et je vérifie qu\'il n\'y a personne.',
    color: '#06b6d4',
  },
  {
    id: 8,
    slug: 'partage-route',
    src: `${IMG_BASE}/08-partage-route.png`,
    title: 'Le partage de la route',
    subtitle: 'Être attentif aux autres usagers',
    category: 'circulation',
    keywords: ['piétons', 'cyclistes', 'motos', 'véhicules lourds'],
    caption: 'Piétons · cyclistes · deux-roues motorisés · véhicules lourds — je respecte leur espace et j\'anticipe leurs réactions.',
    retenir: 'Je reste attentif, je respecte leur espace et j\'anticipe leurs réactions.',
    color: '#22c55e',
  },
  {
    id: 9,
    slug: 'angles-morts',
    src: `${IMG_BASE}/09-angles-morts.png`,
    title: 'Les angles morts',
    subtitle: 'Faire attention aux véhicules plus grands',
    category: 'circulation',
    keywords: ['angles morts', 'camions', 'poids lourds'],
    caption: 'Je reste en dehors des angles morts — surtout près des camions.',
    retenir: 'Je ne reste jamais dans un angle mort, surtout près des camions.',
    color: '#ef4444',
  },
  {
    id: 10,
    slug: 'conditions-circulation',
    src: `${IMG_BASE}/10-conditions-circulation.png`,
    title: 'Les conditions de circulation',
    subtitle: 'S\'adapter à la météo et à la route',
    category: 'circulation',
    keywords: ['pluie', 'brouillard', 'nuit', 'chaussée glissante'],
    caption: 'Pluie · brouillard · nuit · chaussée glissante — j\'adapte vitesse, distances, éclairage et prudence.',
    retenir: 'Les conditions peuvent changer rapidement, j\'adapte ma conduite.',
    color: '#6366f1',
  },
  {
    id: 11,
    slug: 'entretien-vehicule',
    src: `${IMG_BASE}/11-entretien-vehicule.png`,
    title: 'L\'entretien du véhicule',
    subtitle: 'Pour rouler en sécurité',
    category: 'securite',
    keywords: ['pneus', 'freins', 'éclairage', 'niveaux'],
    caption: 'Pneus · freins · éclairage · niveaux (liquide, carburant/batterie) · plaques.',
    retenir: 'Je vérifie régulièrement l\'état de mon véhicule.',
    color: '#f59e0b',
  },
  {
    id: 12,
    slug: 'risques-comportements',
    src: `${IMG_BASE}/12-risques-comportements.png`,
    title: 'Les risques et les bons comportements',
    subtitle: 'Adopter une conduite responsable',
    category: 'securite',
    keywords: ['alcool', 'téléphone', 'fatigue', 'débridage', 'pression'],
    caption: 'Alcool/drogues · téléphone · fatigue · passagers · vitesse · débridage · pression des copains · respect des règles.',
    retenir: 'Mes choix d\'aujourd\'hui construisent ma sécurité de demain.',
    color: '#e11d48',
  },
];

export const AM_CATEGORIES = [
  { key: 'code',        label: 'Code de la route', color: '#a855f7', icon: 'BookOpen' },
  { key: 'circulation', label: 'En circulation',    color: '#22c55e', icon: 'Route' },
  { key: 'securite',    label: 'Sécurité & entretien', color: '#e11d48', icon: 'ShieldCheck' },
];

export function findThemeBySlug(slug) {
  return AM_THEMES.find((t) => t.slug === slug) || null;
}

/** Thèmes réellement affichables (filtre les entrées `hidden: true`). */
export const VISIBLE_AM_THEMES = AM_THEMES.filter((t) => !t.hidden);

export default AM_THEMES;
